import React, { useEffect, useMemo, useState } from "react";
import { format, startOfMonth } from "date-fns";
import { useNavigate, useOutletContext } from "react-router-dom";
import DateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import ModuleHeader, { ModuleHeaderAction } from "../../components/ModuleHeader/ModuleHeader";
import ModulePageShell from "../../components/ModulePageShell/ModulePageShell";
import { exportMerchantOrders, getMerchantOrders } from "../../api/merchantOrderApi";
import "../Module/module.scss";

const statuses = [
  ["", "All Status"],
  ["pending", "Pending"],
  ["paid", "Paid"],
  ["ready_for_pickup", "Ready for Pickup"],
  ["collected", "Collected"],
  ["cancelled", "Cancelled"],
  ["refunded", "Refunded"],
];

const money = (value) => `RM${Number(value || 0).toFixed(2)}`;

const OrdersPage = () => {
  const navigate = useNavigate();
  const { selectedOutlet } = useOutletContext();
  const [dateRange, setDateRange] = useState({ startDate: startOfMonth(new Date()), endDate: new Date() });
  const [status, setStatus] = useState("");
  const [keyword, setKeyword] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const filters = useMemo(() => ({
    outlet_id: selectedOutlet?.id || "",
    start_date: format(dateRange.startDate, "yyyy-MM-dd"),
    end_date: format(dateRange.endDate, "yyyy-MM-dd"),
    status,
    keyword: keyword.trim(),
    per_page: 100,
  }), [dateRange, keyword, selectedOutlet?.id, status]);

  useEffect(() => {
    if (!selectedOutlet?.id) return;
    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError("");
      getMerchantOrders(filters)
        .then((data) => {
          if (active) setOrders(data?.data || []);
        })
        .catch((requestError) => {
          if (active) setError(requestError.message);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [filters, selectedOutlet?.id]);

  const handleExport = async () => {
    setExporting(true);
    setError("");
    try {
      const blob = await exportMerchantOrders(filters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `SecondBite-Orders-${format(new Date(), "yyyy-MM-dd")}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <ModulePageShell className="module-page">
      <ModuleHeader
        bleed
        title="Orders"
        description="Monitor guest checkout orders, payment status, and pickup progress."
        actions={
          <ModuleHeaderAction disabled={exporting} onClick={handleExport}>
            {exporting ? "Exporting..." : "Export Orders"}
          </ModuleHeaderAction>
        }
      />

      {error && <div className="module-message module-message--error">{error}</div>}

      <section className="module-filters module-filters--three">
        <div className="module-filter-field"><span>Date Range</span><DateRangePicker value={dateRange} onChange={setDateRange} /></div>
        <label><span>Order Status</span><select value={status} onChange={(event) => setStatus(event.target.value)}>{statuses.map(([value, label]) => <option value={value} key={label}>{label}</option>)}</select></label>
        <label><span>Keyword</span><input value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="Search order or customer..." /></label>
      </section>

      <section className="module-card">
        <div className="module-card__header"><h3>Orders Listing</h3></div>
        <div className="module-table module-table--orders">
          <div className="module-table__head"><span>Order No</span><span>Customer</span><span>Status</span><span>Pickup Time</span><span>Amount</span><span>Actions</span></div>
          {!loading && orders.map((order) => (
            <button className="module-table__row" type="button" key={order.id} onClick={() => navigate(`/orders/${order.order_number}`)}>
              <span>{order.order_number}</span>
              <span>{order.customer} / {order.customer_phone}</span>
              <span>{order.status_label}</span>
              <span>{order.pickup_window}</span>
              <span>{money(order.amount)}</span>
              <span className="module-table__row-action">View</span>
            </button>
          ))}
        </div>
        {loading && <div className="module-empty">Loading orders...</div>}
        {!loading && orders.length === 0 && <div className="module-empty">No orders found for the selected filters.</div>}
      </section>
    </ModulePageShell>
  );
};

export default OrdersPage;
